// src/context/brandVoice/ui/OwnerChip.jsx — who owns a field's current text.
//
// Four states (plan decision 13, 2026-09-27): `seeded` "Valore di partenza",
// `machine` "Scritto dall'AI", `unlocked` "Sbloccato", `human` "Non modificabile
// dall'IA" — ⛔ never "scritto da te": a frozen field may hold text the AI wrote
// before it was frozen. `state: null` (the backend was not asked, or the ledger is
// unreachable) → NO chip: an absent answer is not a state.
// ⭐ The four look different by LABEL and FILL, not by hue alone — `machine` and
// `unlocked` share the neutral palette on purpose, and `seeded` is the only outlined
// one — so the chip still reads on a monochrome display or for a colour-blind user.
// ⭐ v185: `ctx` is passed through to `ownerChipKey`, so "Sbloccato" disappears on a
// workspace where unlocking has no effect (`unlock_effective: false`, sheet 09).
// ⭐ v196: a FIFTH key, `neverAI` "Non scritto dall'IA" (lock icon + tooltip), is a property of
// the FIELD, not of its owner state: the three "avoid" lists and `verified_claims` are never
// written by a run, so they always carry it, whatever `state` reads.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { ownerChipKey } from '../fieldRules.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../../constants/theme.js';

const LOOK = {
  human: { background: SEMANTIC.viola.bg, color: SEMANTIC.viola.fg, border: `1px solid ${SEMANTIC.viola.fg}` },
  machine: { background: SEMANTIC.neutro.bg, color: SEMANTIC.neutro.fg, border: `1px solid ${SEMANTIC.neutro.fg}` },
  unlocked: { background: SEMANTIC.neutro.bg, color: SEMANTIC.neutro.fg, border: `1px solid ${SEMANTIC.neutro.fg}` },
  neverAI: { background: SEMANTIC.neutro.bg, color: SEMANTIC.neutro.fg, border: `1px solid ${SEMANTIC.neutro.fg}` },
  seeded: { background: 'transparent', color: COLORS.textSecondary, border: `1px dashed ${COLORS.borderStrong}` },
};

export default function OwnerChip({ field, ctx }) {
  const { t } = useTranslation('context');
  const key = ownerChipKey(field, ctx);
  if (!key) return null;
  const neverAI = key === 'neverAI';
  return (
    <span
      title={neverAI ? t('brandVoice.owner.neverAIHint') : undefined}
      style={{
        ...LOOK[key],
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
        borderRadius: RADII.pill, fontFamily: FONT_SANS, fontSize: 11,
        fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      {neverAI && <Lock size={11} aria-hidden="true" />}
      {t(`brandVoice.owner.${key}`)}
    </span>
  );
}
